/**
 * Interview-buffer concern for the Details step.
 *
 * Extracted verbatim from `DetailsStep` (pure file separation — no
 * behaviour change). Owns:
 *   - the account-level buffer fallback fetch (self-schedule-adhoc only),
 *   - one-shot hydration of the buffer fields into shared state,
 *   - the seed-gate reset when leaving the adhoc context,
 *   - the `handleBufferChange` callback.
 *
 * `isBufferEnabledByFlag` stays in `DetailsStep` (used by many other
 * concerns) and is threaded in.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useAccountBuffer } from '../../../api/useAccountBuffer';
import {
  DEFAULT_BUFFER_BEFORE_MINUTES,
  DEFAULT_BUFFER_AFTER_MINUTES,
  coerceBufferMinutes,
} from '../../../calendar/selfSchedule/buffer';
import type { ScheduleSharedState } from '../../../state/useScheduleSharedState';

interface UseDetailsBufferArgs {
  isBufferEnabledByFlag: boolean;
  interviewPlan?: Record<string, unknown>;
  updateSharedState: (_partial: Partial<ScheduleSharedState>) => void;
}

interface UseDetailsBufferResult {
  handleBufferChange: (_next: {
    before: number;
    after: number;
    enabled: boolean;
  }) => void;
}

export function useDetailsBuffer({
  isBufferEnabledByFlag,
  interviewPlan,
  updateSharedState,
}: UseDetailsBufferArgs): UseDetailsBufferResult {
  // Account-level buffer fallback for self-schedule modes without a
  // plan (`self-schedule-adhoc`). Normal self-schedule reads it off
  // the plan response (kula/core#4973 pre-fills `/ats_interviews/new`
  // from `AccountSetting`); this fetch only fires when the flag is on,
  // we're in a self-schedule mode, and `interviewPlan` is missing.
  const { data: accountBuffer } = useAccountBuffer({
    enabled: isBufferEnabledByFlag && !interviewPlan,
  });

  // Hydrate buffer fields into sharedState once when the
  // self-schedule-adhoc account fallback resolves. Guards:
  //   - run only when the fallback is the active source (no plan).
  //   - run only once via the seen-id ref; user edits then take over.
  const adhocBufferSeededRef = useRef(false);
  useEffect(() => {
    if (!isBufferEnabledByFlag || interviewPlan) return;
    if (adhocBufferSeededRef.current) return;
    const cfg = accountBuffer?.interview_buffer_config;
    if (!cfg) return;
    adhocBufferSeededRef.current = true;
    const updates: Partial<ScheduleSharedState> = {};
    const pre = coerceBufferMinutes(cfg.pre_minutes);
    if (pre !== undefined) updates.bufferBeforeMinutes = pre;
    const post = coerceBufferMinutes(cfg.post_minutes);
    if (post !== undefined) updates.bufferAfterMinutes = post;
    if (typeof cfg.enabled === 'boolean') updates.bufferEnabled = cfg.enabled;
    if (Object.keys(updates).length > 0) updateSharedState(updates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBufferEnabledByFlag, interviewPlan, accountBuffer]);

  // Reset the seed gate when leaving self-schedule-adhoc context so a
  // later return to it can re-hydrate from the fresh account response.
  if (!isBufferEnabledByFlag || interviewPlan) {
    adhocBufferSeededRef.current = false;
  }

  const handleBufferChange = useCallback(
    (next: { before: number; after: number; enabled: boolean }) => {
      // Toggle-on with 0/0 → auto-seed sensible defaults so the
      // pill doesn't read "Buffer: 0m before + 0m after" (cosmetic
      // regression for fresh accounts where BE returns 0/0/false
      // and the user just flipped the eye-on without editing).
      const before =
        next.enabled && next.before === 0 && next.after === 0
          ? DEFAULT_BUFFER_BEFORE_MINUTES
          : next.before;
      const after =
        next.enabled && next.before === 0 && next.after === 0
          ? DEFAULT_BUFFER_AFTER_MINUTES
          : next.after;
      updateSharedState({
        bufferBeforeMinutes: before,
        bufferAfterMinutes: after,
        bufferEnabled: next.enabled,
      });
    },
    [updateSharedState]
  );

  return { handleBufferChange };
}
